import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Loader2, Mail, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check if registration is open (no users exist)
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/status`);
        setRegistrationOpen(response.data.registration_open);
      } catch (error) {
        console.error('Failed to check auth status:', error);
        setRegistrationOpen(false);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkAuthStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    if (registrationOpen && !name) {
      toast.error('Please enter your name');
      return;
    }

    if (registrationOpen && password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      if (registrationOpen) {
        // Register first user as admin
        const response = await axios.post(`${API_URL}/api/auth/register`, {
          email,
          password,
          name
        });
        
        const { access_token, user } = response.data;
        localStorage.setItem('edoverify_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        toast.success('Account created! Welcome to EdoVerify Ops Console!');
        navigate('/dashboard');
        window.location.reload(); // Refresh to update auth context
      } else {
        // Normal login
        await login(email, password);
        toast.success('Welcome to EdoVerify Ops Console!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-slate-900"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1740395816813-d20eef5c78b4?crop=entropy&cs=srgb&fm=jpg&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-slate-900/80" />
      
      <Card className="w-full max-w-md relative z-10 shadow-2xl" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img 
              src="/edomatch-logo-black.png" 
              alt="Edomatch" 
              className="h-12 w-auto max-w-[180px] object-contain"
            />
          </div>
          <CardTitle className="font-display text-2xl">Verification Console</CardTitle>
          <CardDescription className="text-base">
            {registrationOpen 
              ? 'Create your admin account to get started'
              : 'Internal Identity Verification Operations'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-4">
          {registrationOpen && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800 font-medium">
                🎉 Welcome! You're the first user - your account will have admin privileges.
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {registrationOpen && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    data-testid="name-input"
                    disabled={loading}
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@edomatch.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="email-input"
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder={registrationOpen ? "Minimum 8 characters" : "••••••••"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  data-testid="password-input"
                  disabled={loading}
                />
              </div>
            </div>
            
            <Button 
              type="submit"
              className="w-full h-12 text-base font-medium"
              disabled={loading}
              data-testid="login-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : null}
              {registrationOpen ? 'Create Admin Account' : 'Sign In'}
            </Button>
          </form>

          <p className="text-xs text-center text-muted-foreground">
            By signing in, you agree to follow Edomatch's security policies and data handling guidelines.
          </p>
          
          <div className="text-xs text-center text-muted-foreground border-t pt-4">
            <p className="font-medium text-slate-600 mb-1">Authorized Personnel Only</p>
            <p>Access is restricted to Edomatch team members with verified accounts.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Login;
